
import { Vue, Component, Prop } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver } from 'vee-validate';
import { DateTime } from 'luxon';
import { Validate } from '@/types';
import { fireGoogleTag } from '@/utils/google-tag-manager-helpers';
import { getDistanceFromLatLonInKm } from '@/utils/haversine';
import CheckoutTakeoutInformation from './form/TakeoutInformation.vue';
import CheckoutDeliveryInformation from './form/DeliveryInformation.vue';
import CheckoutPreOrderDeliveryInformation from './form/PreOrderDeliveryInformation.vue';
import CheckoutCustomQuestions from './form/CustomQuestions.vue';
import CheckoutContactInformation from './form/ContactInformation.vue';
import CheckoutPaymentInformation from './form/PaymentInformation.vue';
import CheckoutTabInformation from './form/TabInformation.vue';
import PoweredByApp8 from '@/components/shared/PoweredByApp8.vue';
import InfoBanner from '@/components/shared/InfoBanner.vue';

const namespace: string = 'cart';

const DEFAULT_PICKUP_INFORMATION = {
	scheduled: false,
	dueByDate: '',
	dueByTime: '',
	notes: ''
};

const DEFAULT_SUITES_INFORMATION = {
	tabLimit: null,
	deliveryTime: '',
	notes: ''
};

const DEFAULT_CONTACT_INFORMATION = {
	full_name: '',
	email: '',
	phone_number: '',
	password: ''
};

const DEFAULT_CARD_INFORMATION = {
	number: '',
	cvd: '',
	expiry_date: '',
	postal_code: '',
	type: ''
};

const DEFAULT_DELIVERY_INFORMATION = {
	type: ''
};

@Component<CheckoutForm>({
	components: {
		CheckoutTakeoutInformation,
		CheckoutDeliveryInformation,
		CheckoutPreOrderDeliveryInformation,
		CheckoutCustomQuestions,
		CheckoutContactInformation,
		CheckoutPaymentInformation,
		CheckoutTabInformation,
		ValidationObserver,
		PoweredByApp8,
		InfoBanner
	}
})
export default class CheckoutForm extends Vue {
	@Prop({ type: Boolean, required: true, default: false }) private gPayReady!: boolean;
	@Prop({ type: Boolean, required: true, default: false }) private aPayReady!: boolean;
	@Prop({ type: Boolean, required: true, default: false }) private isPreOrdering!: boolean;
	@Action('reviewButtonClicked', { namespace }) private reviewButtonClicked!: () => void;
	@Action('updatePickupInformation', { namespace }) private updatePickupInformation!: (pickup: CheckoutPickupInfo) => void;
	@Action('updateSuitesInformation', { namespace }) private updateSuitesInformation!: (suitesInfo: CheckoutSuitesInfo) => void;
	@Action('updateDeliveryInformation', { namespace }) private updateDeliveryInformation!: (delivery: CheckoutDeliveryInfo) => void;
	@Action('updateContactInformation', { namespace }) private updateContactInformation!: (contact: CheckoutContactInfo) => void;
	@Action('updateCardInformation', { namespace }) private updateCardInformation!: (payment: CheckoutCardInfo) => void;
	@Action('updateInvoice', { namespace }) private updateInvoice!: (invoice: boolean) => void;
	@Action('updateCostCenter', { namespace }) private updateCostCenter!: (costCenter: string | null) => void;
	@Action('updatePurchaseOrder', { namespace }) private updatePurchaseOrder!: (purchaseOrder: string | null) => void;
	@Action('updateCustomQuestions', { namespace }) private updateCustomQuestions!: (questions: CheckoutCustomQuestion[]) => void;
	@Action('doesUserExist', { namespace: 'auth' }) private doesUserExist!: (email: string) => Promise<boolean>;
	@Getter('isTakeOut', { namespace }) private isTakeOut!: boolean;
	@Getter('isPickupOnly', { namespace }) private isPickupOnly!: boolean;
	@Getter('isDelivery', { namespace }) private isDelivery!: boolean;
	@Getter('isGenericCatering', { namespace }) private isGenericCatering!: boolean;
	@Getter('isPaymentRequired', { namespace }) private isPaymentRequired!: boolean;
	@Getter('getPickupInformation', { namespace }) private pickupInformation!: CheckoutPickupInfo | null;
	@Getter('getSuitesInformation', { namespace }) private suitesInformation!: CheckoutSuitesInfo | null;
	@Getter('getDeliveryInformation', { namespace }) private deliveryInformation!: CheckoutDeliveryInfo | null;
	@Getter('getContactInformation', { namespace }) private contactInformation!: CheckoutContactInfo | null;
	@Getter('getCardInformation', { namespace }) private cardInformation!: CheckoutCardInfo;
	@Getter('getInvoiceInformation', { namespace }) private invoiceInformation!: boolean;
	@Getter('getCostCenterInformation', { namespace }) private costCenterInformation!: string | null;
	@Getter('getPurchaseOrderInformation', { namespace }) private purchaseOrderInformation!: string | null;
	@Getter('getPaymentMethod', { namespace }) private paymentMethod!: string;
	@Getter('getCheckoutAnsweredQuestions', { namespace }) private checkoutAnsweredQuestions!: CheckoutCustomQuestion[] | null;
	@Getter('getQueueInfo', { namespace }) private queueInfo!: QueueInfo | null;
	@Getter('isAnonymousUser', { namespace: 'auth' }) private isAnonymousUser!: boolean;
	@Getter('getCreditCards', { namespace: 'auth' }) private creditCards!: SavedPaymentOption[];
	@Getter('getRestaurantDeliveryInfo', { namespace: 'restaurant' }) private restaurantDeliveryInfo!: DefaultDelivery | StadiumDelivery | SchoolDelivery | CustomDelivery;
	@Getter('getCustomQuestions', { namespace: 'restaurant' }) private customQuestions!: CustomQuestion[];
	@Getter('isK12Location', { namespace: 'restaurant' }) private isK12Location!: boolean;

	$refs!: {
		observer: Validate,
		customQuestions: CheckoutCustomQuestions
	};

	private pickup: CheckoutPickupInfo = DEFAULT_PICKUP_INFORMATION;
	private suitesInfo: CheckoutSuitesInfo = DEFAULT_SUITES_INFORMATION;
	private contact: CheckoutContactInfo = DEFAULT_CONTACT_INFORMATION;
	private delivery: CheckoutDeliveryInfo = DEFAULT_DELIVERY_INFORMATION;
	private paymentCard: CheckoutCardInfo = DEFAULT_CARD_INFORMATION;
	private invoice: boolean = false;
	private costCenter: string | null = null;
	private purchaseOrder: string | null = null;
	private answeredQuestions: CheckoutCustomQuestion[] = [];
	private coordinatesErrorMsg: string = '';
	private infoBannerMessage: string = '';
	private isValidating: boolean = false;
	private showInfoBanner: boolean = false;

	private get showDelivery(): boolean {
		return this.restaurantDeliveryInfo && (((this.isTakeOut || this.isGenericCatering) && this.isDelivery && !this.isPickupOnly) || this.isK12Location);
	}

	/**
	 * Check if the review button should be disabled.
	 * For now it is only for catering orders: if the user is not logged in, selects CC as a payment method while not creating an account at checkout.
	 *
	 * @return {boolean}
	 */
	private get isReviewButtonDisabled(): boolean {
		return this.isGenericCatering && this.isAnonymousUser && this.paymentMethod === 'credit_card' && !this.contact.password;
	}

	/**
	 * Set information on component mount if there is any
	 * to fill up the fields if they were filled up previously
	 * TODO: Update this to not directly assign data to the vuex store values, it mutates the state and causes reactivity issues
	 *
	 * @return {void}
	 */
	private mounted(): void {
		if(this.pickupInformation) {
			this.pickup = this.pickupInformation;
		}
		if(this.suitesInformation) {
			this.suitesInfo = this.suitesInformation;
		}
		if(this.deliveryInformation) {
			this.delivery = this.deliveryInformation;
		}
		if(this.contactInformation) {
			this.contact = this.contactInformation;
		}
		if(this.checkoutAnsweredQuestions?.length) {
			this.answeredQuestions = [ ...this.checkoutAnsweredQuestions ];
		}

		// Payment info
		if(this.invoiceInformation) {
			this.invoice = this.invoiceInformation;
		}
		else if(this.costCenterInformation) {
			this.costCenter = this.costCenterInformation;
		}
		else if(this.purchaseOrderInformation) {
			this.purchaseOrder = this.purchaseOrderInformation;
		}
		else if(this.cardInformation) {
			this.paymentCard = this.cardInformation;
		}

		if(typeof this.queueInfo?.minutesLeft === 'number' && typeof this.queueInfo?.queueSize === 'number') {
			this.showInfoBanner = true;
			this.infoBannerMessage = this.$t('checkout.form.takeout.queue_info', { minutes: this.queueInfo.minutesLeft, orders: this.queueInfo.queueSize });
		}
	}

	/**
	 * Update pickup info on input changed
	 *
	 * @param {CheckoutPickupInfo} pickup
	 * @return {void}
	 */
	private updateTempPickupInfo(pickup: CheckoutPickupInfo): void {
		this.pickup = pickup;
	}

	/**
	 * Update suites info on input changed
	 *
	 * @param {CheckoutSuitesInfo} suitesInfo
	 * @return {void}
	 */
	private updateTempSuitesInfo(suitesInfo: CheckoutSuitesInfo): void {
		this.suitesInfo.deliveryTime = suitesInfo.deliveryTime;
		this.suitesInfo.notes = suitesInfo.notes;
	}

	/**
	 * Update tab limit in suites info
	 *
	 * @param {number} tabLimit
	 * @returns {void}
	 */
	private updateTabInfo(tabLimit: number): void {
		this.suitesInfo.tabLimit = tabLimit;
	}

	/**
	 * Update contact info on input changed
	 *
	 * @param {CheckoutContactInfo} contact
	 * @return {void}
	 */
	private updateTempContactInfo(contact: CheckoutContactInfo): void {
		this.contact = contact;
	}

	/**
	 * Update payment info on input changed
	 *
	 * @param {CheckoutCardInfo} tempPaymentCard
	 * @return {void}
	 */
	private updateTempPaymentCardInfo(tempPaymentCard: CheckoutCardInfo): void {
		this.paymentCard = tempPaymentCard;
	}

	/**
	 * Update invoice on input changed
	 *
	 * @param {boolean} tempInvoice
	 * @return {void}
	 */
	private updateTempInvoice(tempInvoice: boolean): void {
		this.invoice = tempInvoice;
	}

	/**
	 * Update cost center on input changed
	 *
	 * @param {string | null} tempCostCenter
	 * @return {void}
	 */
	private updateTempCostCenter(tempCostCenter: string | null): void {
		this.costCenter = tempCostCenter;
	}

	/**
	 * Update purchase order on input changed
	 *
	 * @param {string | null} tempPurchaseOrder
	 * @return {void}
	 */
	private updateTempPurchaseOrder(tempPurchaseOrder: string | null): void {
		this.purchaseOrder = tempPurchaseOrder;
	}

	/**
	 * Update delivery info on input changed
	 *
	 * @param {CheckoutDeliveryInfo} payment
	 * @return {void}
	 */
	private updateTempDeliveryInfo(delivery: CheckoutDeliveryInfo): void {
		this.coordinatesErrorMsg = '';
		this.delivery = delivery;
	}

	/**
	 * Update custom questions on input changed
	 *
	 * @param {CheckoutCustomQuestion[]} questions
	 * @return {void}
	 */
	private updateTempCustomQuestions(questions: CheckoutCustomQuestion[]): void {
		this.answeredQuestions = questions;
		this.answeredQuestions.length && this.updateCustomQuestions(this.answeredQuestions);
	}

	/**
	 * Detect which card type it is with regex
	 *
	 * @param {string} cardNumber
	 * @return {void}
	 */
	private findCardType(cardNumber: string): void {
		const amex_regex = new RegExp('^3[47][0-9]{0,}$'); // American Express: 34, 37
		const visa_regex = new RegExp('^4[0-9]{0,}$'); // Visa: 4
		const mc_regex = new RegExp('^(5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[01]|2720)[0-9]{0,}$'); // Mastercard: 2221-2720, 51-55
		if(cardNumber.match(amex_regex)) {
			this.paymentCard.type = 'AMEX';
		}
		if(cardNumber.match(mc_regex)) {
			this.paymentCard.type = 'MC';
		}
		if(cardNumber.match(visa_regex)) {
			this.paymentCard.type = 'VISA';
		}
	}

	/**
	 * Validate all the input fields, save the pickup information if it's
	 * a take out order and save the user contact information if it's
	 * an anonymous order and proceed to the review screen.
	 *
	 * @return {Promise<void>}
	 */
	private async saveAndContinue(): Promise<any> {
		if (this.isReviewButtonDisabled) {
			return;
		}
		this.isValidating = true;
		this.reviewButtonClicked();
		let isValid = await this.$refs.observer.validate();
		let deliveryValid = true;

		// Validate delivery radius
		if(this.pickup.delivery) {
			deliveryValid = this.validateDelivery();
		}

		// Validate that the user is not trying to use an email that is already in use when creating an account
		if (this.contactInformation?.password && await this.doesUserExist(this.contactInformation.email)) {
			this.$refs.observer.setErrors({
				email: [this.$t('checkout.form.contact.email_unique_error')]
			});
			isValid = false;
		}

		// If a payment method isn't chosen, we do not go next (unless it's pay on premise, then we don't care)
		if(this.isPaymentRequired && !this.paymentMethod) {
			setTimeout(() => {
				const requiredLabel = document.getElementById('payment-method-header') as HTMLElement;
				requiredLabel.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
			}, 150);
			this.isValidating = false;
			return;
		}

		// If everything is validated, we can go next.
		if (isValid && deliveryValid) {
			this.coordinatesErrorMsg = '';
			this.setDueDateTimeIfAsapOrder();
			this.updatePickupInformation(this.pickup);
			this.updateDeliveryInformation(this.delivery);

			// Update suites info if pre-ordering
			if(this.isPreOrdering) {
				this.updateSuitesInformation(this.suitesInfo);
			}

			// If anon user, update its contact information
			if(this.isAnonymousUser) {
				this.updateContactInformation(this.contact);
			}

			// If the anon user or WBL user with no card added a credit card, find its type and update
			if (this.paymentMethod === 'credit_card' && (this.isAnonymousUser || !this.creditCards.length)) {
				this.findCardType(this.paymentCard.number.replace(/\D/g,''));
				this.updateCardInformation(this.paymentCard);
			}

			if(this.paymentMethod === 'invoice') {
				this.updateInvoice(true);
			}
			else if(this.paymentMethod === 'cost_center') {
				this.updateCostCenter(this.costCenter);
			}
			else if(this.paymentMethod === 'purchase_order') {
				this.updatePurchaseOrder(this.purchaseOrder);
			}

			this.$emit('next');
		}
		else {
			setTimeout(() => {
				const requiredLabel = document.getElementsByClassName('error-label')[0] as HTMLElement;
				requiredLabel.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
			}, 150);
		}
		this.isValidating = false;
	}

	/**
	 * Set the due date and time for an asap order
	 *
	 * @return {void}
	 */
	private setDueDateTimeIfAsapOrder(): void {
		if ((this.isTakeOut || this.isGenericCatering) && !this.pickup.scheduled) {
			this.pickup.dueByDate = DateTime.local().toISO()!;
			this.pickup.dueByTime = DateTime.local().toFormat(this.$t('checkout.confirmation.time_format'));
			if (this.pickup.dueByTime.length == 7) {
				this.pickup.dueByTime = '0' + this.pickup.dueByTime;
			}
		}
	}

	/**
	 * Validate delivery depending of its type
	 *
	 * @return {boolean}
	 */
	private validateDelivery(): boolean {
		const tempDelivery: CheckoutDeliveryInfo = this.delivery as CheckoutDeliveryInfo;
		this.coordinatesErrorMsg = '';
		if (tempDelivery.type === 'address' && this.validateAddressDelivery(tempDelivery)) {
			return true;
		}
		else if (tempDelivery.type === 'open_address' && tempDelivery.address) {
			return true;
		}
		else if (tempDelivery.type === 'stadium' && tempDelivery.section && tempDelivery.row && tempDelivery.seat) {
			return true;
		}
		else if (tempDelivery.type === 'school' && this.validateSchoolDelivery(tempDelivery)) {
			return true;
		}
		else if (tempDelivery.location && tempDelivery.area){
			return true;
		}
		return false;
	}

	/**
	 * Validate delivery of address type.
	 *
	 * @param {CheckoutDeliveryInfo} delivery
	 * @return {boolean}
	 */
	private validateAddressDelivery(delivery: CheckoutDeliveryInfo): boolean {
		if(delivery.lat && delivery.lng) {
			if(delivery.streetNumber) {
				const defaultDelivery: DefaultDelivery = this.restaurantDeliveryInfo;
				const distance = getDistanceFromLatLonInKm(delivery.lat, delivery.lng, defaultDelivery.lat!, defaultDelivery.lng!);
				if(distance > delivery.radius!) {
					this.coordinatesErrorMsg = this.$t('checkout.form.delivery.error_address_exceed_radius');
					return false;
				}
				return true;
			}
			this.coordinatesErrorMsg = this.$t('checkout.form.delivery.error_address_not_precise');
			return false;
		}
		this.coordinatesErrorMsg = this.$t('checkout.form.delivery.error_address_invalid');
		return false;
	}

	/**
	 * Validate delivery of school type:
	 * - If there is only 1 school setup we don't ask for the school name
	 * - Grades and Classrooms are optional
	 * - Teachers is optional and can also be an empty string so we have to be more specific
	 * - Student is mandatory
	 *
	 * @param {CheckoutDeliveryInfo} delivery
	 * @return {boolean}
	 */
	private validateSchoolDelivery(delivery: CheckoutDeliveryInfo): boolean {
		const schoolNameRequired: boolean = (this.restaurantDeliveryInfo as SchoolDelivery).schools.length > 1;
		const areThereGrades: boolean = (this.restaurantDeliveryInfo as SchoolDelivery).schools.some((school: SchoolDeliveryElement) => school.grades);
		const areThereClassrooms: boolean = (this.restaurantDeliveryInfo as SchoolDelivery).schools.some((school: SchoolDeliveryElement) => school.classrooms);
		const areThereTeachers: boolean = (this.restaurantDeliveryInfo as SchoolDelivery).schools.some((school: SchoolDeliveryElement) => school.teachers !== undefined);

		if(delivery.student
			&& ((schoolNameRequired && delivery.school) || !schoolNameRequired)
			&& ((areThereGrades && delivery.grade) || !areThereGrades)
			&& ((areThereClassrooms && delivery.classroom) || !areThereClassrooms)
			&& ((areThereTeachers && delivery.teacher) || !areThereTeachers)
		) {
			return true;
		}
		return false;
	}

	/**
	 * Update the timepicker questions (time options and selected time) whenever we update the pickup date/time.
	 *
	 * @param {boolean|undefined} isFirstLoad
	 * @return {void}
	 */
	private updateTimepickerQuestions(isFirstLoad: boolean|undefined): void {
		this.$refs.customQuestions?.updateTimepickerQuestions(isFirstLoad);
	}

	/**
	 * Redirect to login page
	 *
	 * @return {void}
	 */
	private redirectToLogin(): void {
		fireGoogleTag({ name: 'viewProfile', userType: 'guest' });
		this.$router.push({ path: '/login', query: this.$route.query, hash: this.$route.hash }).catch(() => {});
	}
}
